import { useCallback, useEffect, useState } from "preact/hooks";
import type { JSX } from "preact";
import { api, ApiError, type Operator, type Site, type Workspace } from "../../api";
import { Avatar } from "../../components/Avatar";
import { MoreIcon } from "../../components/Icons";
import { Menu } from "../../components/Menu";
import { useDialogs } from "../../components/Dialog";
import { useI18n } from "../../lib/i18n";
import { errorCodeOf } from "../../lib/apiError";
import { PlanLimitNotice, usePlanLimit } from "../../components/PlanNotice";
import { Modal, Section, SettingRow, StatusLine, Switch, useSites, useStatus } from "./common";

type OperatorRow = Operator & {
  twoFactorEnabled: boolean;
  isActive: boolean;
  siteIds: number[];
  presence: string;
};

/**
 * The team. Admins and owners see every site; an agent sees only the sites
 * ticked here — which is why a new agent without a site sees an empty inbox,
 * and why the form asks.
 *
 * Everything a locked-out teammate needs is on their row: a new password
 * (by link or typed), a fresh start on two-step verification, and — for the
 * account that should not exist at all — delete.
 */

function SiteChecks({
  sites,
  value,
  onChange,
}: {
  sites: Site[];
  value: number[];
  onChange: (next: number[]) => void;
}): JSX.Element {
  return (
    <div class="checks">
      {sites.map((site) => (
        <label key={site.id} class="check-item">
          <input
            type="checkbox"
            checked={value.includes(site.id)}
            onChange={(e) =>
              onChange(e.currentTarget.checked ? [...value, site.id] : value.filter((id) => id !== site.id))
            }
          />
          {site.name}
        </label>
      ))}
    </div>
  );
}

const EMPTY_DRAFT = {
  email: "",
  displayName: "",
  password: "",
  role: "agent",
  siteIds: [] as number[],
  invite: true,
};

export function OperatorsTab({ operator }: { operator: Operator }): JSX.Element {
  const { t: all } = useI18n();
  const t = all.team;
  const dialogs = useDialogs();
  const { sites } = useSites();
  const [rows, setRows] = useState<OperatorRow[]>([]);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [adding, setAdding] = useState(false);
  const [status, show, fail] = useStatus();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [workspaceName, setWorkspaceName] = useState("");
  // Adding or reactivating an operator past the seat count is a 402, not a
  // bad request: it needs an upgrade, so it gets the upgrade notice.
  const { limit, catchLimit, clearLimit } = usePlanLimit();

  const report = useCallback(
    (err: unknown): void => {
      if (!catchLimit(err)) fail(err);
    },
    [catchLimit, fail],
  );

  useEffect(() => {
    void api
      .workspace()
      .then((r) => {
        setWorkspace(r.workspace);
        setWorkspaceName(r.workspace.name);
      })
      .catch(report);
  }, [report]);

  async function setAutoAssign(mode: Workspace["autoAssign"]): Promise<void> {
    try {
      setWorkspace((await api.updateWorkspace({ autoAssign: mode })).workspace);
      show("ok", t.autoSaved);
    } catch (err) {
      report(err);
    }
  }

  async function saveWorkspaceName(event: Event): Promise<void> {
    event.preventDefault();
    try {
      const next = (await api.updateWorkspace({ name: workspaceName.trim() })).workspace;
      setWorkspace(next);
      setWorkspaceName(next.name);
      show("ok", t.workspaceSaved);
    } catch (err) {
      report(err);
    }
  }

  const reload = useCallback(async () => {
    try {
      setRows((await api.operators()).operators);
    } catch (err) {
      report(err);
    }
  }, [report]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function create(event: Event): Promise<void> {
    event.preventDefault();
    const name = draft.displayName.trim();
    const email = draft.email.trim();
    try {
      clearLimit();
      await api.createOperator({
        email,
        displayName: name,
        role: draft.role,
        siteIds: draft.siteIds,
        // Left out on purpose when inviting: the server then mails a link and
        // nobody here ever sees the password.
        ...(draft.invite ? {} : { password: draft.password }),
      });
      show("ok", draft.invite ? t.invited(name, email) : t.added(name));
      setDraft({ ...EMPTY_DRAFT, invite: draft.invite });
      setAdding(false);
      await reload();
    } catch (err) {
      // The account was made; only the e-mail failed. Saying "couldn't add"
      // would send the admin off to add the same person twice.
      if (err instanceof ApiError && err.status === 502 && errorCodeOf(err) === "mail_failed") {
        show("error", t.inviteMailFailed(name));
        setDraft({ ...EMPTY_DRAFT, invite: draft.invite });
        setAdding(false);
        await reload();
        return;
      }
      report(err);
    }
  }

  async function update(row: OperatorRow, patch: Record<string, unknown>, done: string): Promise<void> {
    try {
      await api.updateOperator(row.id, patch);
      show("ok", done);
      await reload();
    } catch (err) {
      report(err);
    }
  }

  async function edit(row: OperatorRow): Promise<void> {
    const values = await dialogs.form({
      title: t.editTitle(row.displayName),
      body: t.editBody,
      confirmLabel: all.common.save,
      fields: [
        {
          key: "displayName",
          label: t.name,
          initial: row.displayName,
          maxLength: 40,
          minLength: 2,
        },
        {
          key: "email",
          label: t.email,
          type: "email",
          initial: row.email,
          maxLength: 200,
        },
      ],
    });
    if (!values) return;
    const patch: Record<string, unknown> = {};
    if (values["displayName"] !== row.displayName) patch["displayName"] = values["displayName"];
    if (values["email"]?.toLowerCase() !== row.email.toLowerCase()) patch["email"] = values["email"];
    if (Object.keys(patch).length === 0) return;
    await update(row, patch, t.memberSaved(values["displayName"] ?? row.displayName));
  }

  async function sendResetLink(row: OperatorRow): Promise<void> {
    try {
      const res = await api.resetOperatorPassword(row.id);
      show("ok", t.resetLinkSent(res.email));
    } catch (err) {
      report(err);
    }
  }

  async function setTemporaryPassword(row: OperatorRow): Promise<void> {
    const password = await dialogs.prompt({
      title: t.resetTempTitle(row.displayName),
      body: t.resetBody,
      label: t.tempPassword,
      hint: all.security.nextHint,
      type: "password",
      minLength: 12,
      maxLength: 200,
      confirmLabel: all.common.save,
    });
    if (!password) return;
    await update(row, { password }, t.resetTempSaved(row.displayName));
  }

  async function resetTwoFactor(row: OperatorRow): Promise<void> {
    const sure = await dialogs.confirm({
      title: t.reset2faTitle(row.displayName),
      body: t.reset2faBody,
      confirmLabel: t.reset2fa,
      danger: true,
    });
    if (!sure) return;
    try {
      await api.resetOperatorTwoFactor(row.id);
      show("ok", t.reset2faDone(row.displayName));
      await reload();
    } catch (err) {
      report(err);
    }
  }

  async function remove(row: OperatorRow): Promise<void> {
    const sure = await dialogs.confirm({
      title: t.deleteMemberTitle(row.displayName),
      body: t.deleteMemberBody,
      confirmLabel: all.common.delete,
      danger: true,
      typeToConfirm: row.email,
    });
    if (!sure) return;
    try {
      await api.deleteOperator(row.id);
      show("ok", t.memberDeleted(row.displayName));
      await reload();
    } catch (err) {
      report(err);
    }
  }

  const canInviteOrAdd =
    draft.displayName.trim().length >= 2 &&
    !!draft.email &&
    (draft.invite || draft.password.length >= 12) &&
    (draft.role !== "agent" || draft.siteIds.length > 0);

  return (
    <>
      <PlanLimitNotice limit={limit} />
      <StatusLine status={status} />
      {workspace ? (
        <Section title={t.workspaceTitle}>
          <SettingRow title={t.workspaceName} hint={t.workspaceNameHint} htmlFor="ws-name">
            <form onSubmit={(e) => void saveWorkspaceName(e)} class="inline-actions">
              <input
                id="ws-name"
                type="text"
                value={workspaceName}
                maxLength={120}
                onInput={(e) => setWorkspaceName(e.currentTarget.value)}
              />
              <button class="btn primary" disabled={!workspaceName.trim() || workspaceName.trim() === workspace.name}>
                {all.common.save}
              </button>
            </form>
          </SettingRow>
        </Section>
      ) : null}
      {workspace ? (
        <Section title={t.autoTitle} intro={t.autoIntro}>
          <SettingRow title={t.autoRow}>
            <div class="radio-list" role="radiogroup" aria-label={t.autoTitle}>
              {(["off", "balanced"] as const).map((mode) => (
                <label key={mode} class="check-item">
                  <input
                    type="radio"
                    name="auto-assign"
                    checked={workspace.autoAssign === mode}
                    onChange={() => void setAutoAssign(mode)}
                  />
                  {mode === "off" ? t.autoOff : t.autoBalanced}
                </label>
              ))}
            </div>
          </SettingRow>
        </Section>
      ) : null}
      <Section
        title={t.members}
        actions={
          <button class="btn primary" onClick={() => setAdding(true)}>
            + {t.addTitle}
          </button>
        }
      >
        <div class="team">
          {rows.map((row) => {
            // An owner's account is an owner's to touch; the server says the
            // same, this only saves the click.
            const locked = row.role === "owner" && operator.role !== "owner";
            return (
              <div key={row.id} class={`member${row.isActive ? "" : " inactive"}`}>
                <Avatar name={row.displayName} color={row.color} url={row.avatarUrl} size={36} />
                <div class="who">
                  <strong>
                    {row.displayName}
                    {row.id === operator.id ? <span class="hint"> {t.you}</span> : null}
                  </strong>
                  <span class="hint">{row.email}</span>
                  <span class="tags">
                    <span class="chip">{all.role[row.role]}</span>
                    {row.twoFactorEnabled ? (
                      <span class="chip verified">{t.twoFactorOn}</span>
                    ) : (
                      <span class="chip">{t.twoFactorOff}</span>
                    )}
                    {row.isActive ? null : <span class="chip">{t.inactive}</span>}
                  </span>
                  {row.role === "agent" ? (
                    <div style={{ marginTop: "8px" }}>
                      <span class="hint">{t.sitesVisible}</span>
                      <SiteChecks
                        sites={sites}
                        value={row.siteIds}
                        onChange={(siteIds) => void update(row, { siteIds }, t.sitesChanged(row.displayName))}
                      />
                    </div>
                  ) : (
                    <span class="hint">{t.allSites}</span>
                  )}
                </div>
                {row.id === operator.id ? null : (
                  <div class="member-actions">
                    <select
                      aria-label={t.roleOf(row.displayName)}
                      value={row.role}
                      disabled={locked}
                      onChange={(e) =>
                        void update(row, { role: e.currentTarget.value }, t.roleChanged(row.displayName))
                      }
                    >
                      <option value="agent">{all.role.agent}</option>
                      <option value="admin">{all.role.admin}</option>
                      {operator.role === "owner" ? <option value="owner">{all.role.owner}</option> : null}
                    </select>
                    <button
                      class="btn sm"
                      disabled={locked}
                      onClick={() =>
                        void update(
                          row,
                          { isActive: !row.isActive },
                          row.isActive ? t.deactivated(row.displayName) : t.activated(row.displayName),
                        )
                      }
                    >
                      {row.isActive ? t.deactivate : t.activate}
                    </button>
                    {locked ? null : (
                      <Menu label={t.moreActions(row.displayName)} buttonClass="btn ghost icon" button={<MoreIcon />}>
                        {(close) => (
                          <>
                            <button
                              role="menuitem"
                              onClick={() => {
                                close();
                                void edit(row);
                              }}
                            >
                              {t.editMember}
                            </button>
                            <hr />
                            <button
                              role="menuitem"
                              disabled={!row.isActive}
                              onClick={() => {
                                close();
                                void sendResetLink(row);
                              }}
                            >
                              {t.resetSendLink}
                            </button>
                            <button
                              role="menuitem"
                              onClick={() => {
                                close();
                                void setTemporaryPassword(row);
                              }}
                            >
                              {t.resetSetTemp}
                            </button>
                            <button
                              role="menuitem"
                              disabled={!row.twoFactorEnabled}
                              onClick={() => {
                                close();
                                void resetTwoFactor(row);
                              }}
                            >
                              {t.reset2fa}
                            </button>
                            <hr />
                            <button
                              role="menuitem"
                              class="danger"
                              onClick={() => {
                                close();
                                void remove(row);
                              }}
                            >
                              {t.deleteMember}
                            </button>
                          </>
                        )}
                      </Menu>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      {adding ? (
        <Modal title={t.addTitle} intro={t.addIntro} wide onClose={() => setAdding(false)}>
          <form onSubmit={(e) => void create(e)} class="grid-2">
            <label>
              {t.name}
              <input
                type="text"
                value={draft.displayName}
                maxLength={40}
                onInput={(e) => setDraft({ ...draft, displayName: e.currentTarget.value })}
                required
              />
            </label>
            <label>
              {t.email}
              <input
                type="email"
                value={draft.email}
                onInput={(e) => setDraft({ ...draft, email: e.currentTarget.value })}
                required
              />
            </label>
            <label>
              {t.role}
              <select value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.currentTarget.value })}>
                <option value="agent">{t.roleAgent}</option>
                <option value="admin">{t.roleAdmin}</option>
                {operator.role === "owner" ? <option value="owner">{all.role.owner}</option> : null}
              </select>
            </label>
            {draft.invite ? (
              <span />
            ) : (
              <label>
                {t.tempPassword} <span class="hint">{all.security.nextHint}</span>
                <input
                  type="password"
                  minLength={12}
                  autocomplete="new-password"
                  value={draft.password}
                  onInput={(e) => setDraft({ ...draft, password: e.currentTarget.value })}
                  required
                />
              </label>
            )}
            <div class="span-2">
              <Switch
                label={t.inviteByMail}
                hint={t.inviteByMailHint}
                checked={draft.invite}
                onChange={(invite) => setDraft({ ...draft, invite, password: "" })}
              />
            </div>
            {draft.role === "agent" ? (
              <div class="span-2">
                <span class="hint">{t.sitesToSee}</span>
                <SiteChecks
                  sites={sites}
                  value={draft.siteIds}
                  onChange={(siteIds) => setDraft({ ...draft, siteIds })}
                />
              </div>
            ) : null}
            <div class="span-2 dialog-actions">
              <button type="button" class="btn" onClick={() => setAdding(false)}>
                {all.common.cancel}
              </button>
              <button class="btn primary" disabled={!canInviteOrAdd}>
                {t.add}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}
      {dialogs.dialog}
    </>
  );
}
